/**
 * MathMarkup → a plain view tree that MathText maps 1:1 to elements.
 *
 * The core parser (parseMarkup) gives a flat-ish AST where scripts follow
 * their base as siblings. The view tree is what the renderer wants:
 * - a subscript and superscript on the same base become one stacked
 *   `subsup` node (Σ_{k=1}^{9}, not two scripts side by side);
 * - leading/trailing spaces inside every group are dropped (spacing comes
 *   from the explicit spaces in the markup);
 * - the answer blank can be filled with a value (the "filled box" shown when
 *   the answer is revealed, and on the block after a correct answer).
 *
 * Also here: how a value is written as math (stacked fraction, U+2212 minus,
 * decimal comma in Afrikaans) and the answer reveal shown after a wrong answer
 * or a timeout ("x = 4", "T₈ = 29", or the prompt with its box filled in).
 */
import {
  formatRational,
  parseMarkup,
  toSpeech,
  type AnswerKind,
  type Locale,
  type MathMarkup,
  type MNode,
  type Problem,
  type Rational,
} from '@dropstack/core';

export type MathView =
  | { kind: 'num'; text: string }
  | { kind: 'var'; text: string }
  | { kind: 'op'; text: string }
  | { kind: 'fn'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'sym'; text: string }
  | { kind: 'space' }
  | { kind: 'sep' }
  | { kind: 'box'; fill: MathView[] | null }
  | { kind: 'sup'; children: MathView[] }
  | { kind: 'sub'; children: MathView[] }
  | { kind: 'subsup'; sub: MathView[]; sup: MathView[] }
  | { kind: 'frac'; num: MathView[]; den: MathView[] }
  | { kind: 'sqrt'; index: MathView[] | null; body: MathView[] };

export interface MathViewOptions {
  /** Draw every answer blank filled with this content. */
  fill?: MathView[] | null;
}

/** Remove spaces at both ends of a group. */
function trimSpaces(nodes: MathView[]): MathView[] {
  let start = 0;
  let end = nodes.length;
  while (start < end && nodes[start]!.kind === 'space') start++;
  while (end > start && nodes[end - 1]!.kind === 'space') end--;
  return start === 0 && end === nodes.length ? nodes : nodes.slice(start, end);
}

/** Transform a parsed MathMarkup AST into the view tree. Pure. */
export function toMathView(nodes: readonly MNode[], options: MathViewOptions = {}): MathView[] {
  const fill = options.fill ?? null;
  const group = (children: readonly MNode[]) => toMathView(children, options);
  const out: MathView[] = [];

  for (const node of nodes) {
    switch (node.t) {
      case 'num':
      case 'var':
      case 'op':
      case 'fn':
      case 'text':
      case 'sym':
        out.push({ kind: node.t, text: node.v });
        break;
      case 'space':
        // The parser already merges runs of spaces; never emit two in a row (e.g. around a dropped group).
        if (out.at(-1)?.kind !== 'space') out.push({ kind: 'space' });
        break;
      case 'sep':
        out.push({ kind: 'sep' });
        break;
      case 'box':
        out.push({ kind: 'box', fill });
        break;
      case 'frac':
        out.push({ kind: 'frac', num: group(node.num), den: group(node.den) });
        break;
      case 'sqrt':
        out.push({ kind: 'sqrt', index: node.idx ? group(node.idx) : null, body: group(node.c) });
        break;
      case 'sup':
      case 'sub': {
        const children = group(node.c);
        const prev = out.at(-1);
        // A script right after the opposite script on the same base: stack them.
        if (node.t === 'sup' && prev?.kind === 'sub') out[out.length - 1] = { kind: 'subsup', sub: prev.children, sup: children };
        else if (node.t === 'sub' && prev?.kind === 'sup') out[out.length - 1] = { kind: 'subsup', sub: children, sup: prev.children };
        else out.push({ kind: node.t, children });
        break;
      }
    }
  }
  return trimSpaces(out);
}

/** Parse and transform markup. Invalid markup (never expected from the templates) degrades to plain text. */
export function markupView(markup: MathMarkup, options: MathViewOptions = {}): MathView[] {
  try {
    return toMathView(parseMarkup(markup), options);
  } catch {
    return [{ kind: 'text', text: markup }];
  }
}

// ───────────── Values ─────────────

/** A value written as math, as the problem expects it: "−3" → minus + 3, "3/4" → stacked fraction, "0,75" in Afrikaans. */
export function valueView(value: Rational, kind: AnswerKind, locale: Locale): MathView[] {
  const text = formatRational(value, kind, locale);
  const negative = text.startsWith('−');
  const body = negative ? text.slice(1) : text;
  const slash = body.indexOf('/');
  const core: MathView =
    slash >= 0
      ? { kind: 'frac', num: [{ kind: 'num', text: body.slice(0, slash) }], den: [{ kind: 'num', text: body.slice(slash + 1) }] }
      : { kind: 'num', text: body };
  return negative ? [{ kind: 'op', text: '−' }, core] : [core];
}

/** The same value as (English) MathMarkup, for speech: "-\frac{3}{4}", "0.75", "12". */
export function valueMarkup(value: Rational, kind: AnswerKind): MathMarkup {
  const text = formatRational(value, kind, 'en').replace('−', '-');
  const slash = text.indexOf('/');
  if (slash < 0) return text;
  const negative = text.startsWith('-');
  const num = text.slice(negative ? 1 : 0, slash);
  return `${negative ? '-' : ''}\\frac{${num}}{${text.slice(slash + 1)}}`;
}

/** Screen-reader text for markup, or null if it doesn't parse. Afrikaans reads decimals with a comma, as they are written. */
function trySpeech(markup: MathMarkup, locale: Locale): string | null {
  try {
    const text = toSpeech(markup, locale);
    return locale === 'af' ? text.replace(/(\d)\.(\d)/g, '$1,$2') : text;
  } catch {
    return null;
  }
}

/** Screen-reader text for markup (the raw markup if it can't be parsed). */
export function speechFor(markup: MathMarkup, locale: Locale): string {
  return trySpeech(markup, locale) ?? markup;
}

// ───────────── Answer reveal ─────────────

export type RevealProblem = Pick<Problem, 'prompt' | 'instruction' | 'answer' | 'answerKind'>;

export interface AnswerReveal {
  view: MathView[];
  /** What a screen reader says, e.g. "x equals 4". */
  speech: string;
}

const BOX_MARKUP = /\\box(?![a-zA-Z])|□/g;
const RELATIONS = new Set(['=', '<', '>', '≤', '≥', '≠', '⇒']);
const isVariable = (v: unknown): v is string => typeof v === 'string' && /^[a-zA-Z]$/.test(v);
const isCount = (v: unknown): v is number | string => /^\d{1,4}$/.test(String(v));

function hasBox(nodes: readonly MNode[]): boolean {
  return nodes.some((n) => {
    switch (n.t) {
      case 'box':
        return true;
      case 'sup':
      case 'sub':
        return hasBox(n.c);
      case 'frac':
        return hasBox(n.num) || hasBox(n.den);
      case 'sqrt':
        return hasBox(n.c) || (n.idx !== null && hasBox(n.idx));
      default:
        return false;
    }
  });
}

function collectVariables(nodes: readonly MNode[], into: Set<string>): Set<string> {
  for (const n of nodes) {
    if (n.t === 'var') into.add(n.v);
    else if (n.t === 'sup' || n.t === 'sub') collectVariables(n.c, into);
    else if (n.t === 'frac') collectVariables([...n.num, ...n.den], into);
    else if (n.t === 'sqrt') collectVariables([...n.c, ...(n.idx ?? [])], into);
  }
  return into;
}

/**
 * Left-hand side for "<lhs> = <answer>", or null to show the bare value.
 * The instruction says what the answer is (the variable to solve for, term n,
 * the sum of n terms, the discriminant); otherwise an expression without a
 * relation is completed ("2n + 2 = 4") and an equation in one variable names it.
 */
function revealLhs(problem: RevealProblem, nodes: readonly MNode[]): MathMarkup | null {
  const key = problem.instruction?.key;
  const params = problem.instruction?.params ?? {};
  if ((key === 'solve' || key === 'findY' || key === 'simultaneous') && isVariable(params.v)) return params.v;
  if (key === 'term' && isCount(params.n)) return `T_{${params.n}}`;
  if (key === 'sum' && isCount(params.n)) return `S_{${params.n}}`;
  if (key === 'discriminant') return '\\Delta';
  if (!nodes.some((n) => n.t === 'op' && RELATIONS.has(n.v)) && !nodes.some((n) => n.t === 'sep')) return problem.prompt;
  const vars = [...collectVariables(nodes, new Set())];
  return vars.length === 1 ? vars[0]! : null;
}

/**
 * The correct answer, shown kindly after a wrong answer or a timeout: the
 * prompt with its box filled in, "x = 4" for an equation, or just the value.
 */
export function answerReveal(problem: RevealProblem, locale: Locale): AnswerReveal {
  const value = valueView(problem.answer, problem.answerKind, locale);
  const spokenValue = valueMarkup(problem.answer, problem.answerKind);
  let nodes: MNode[];
  try {
    nodes = parseMarkup(problem.prompt);
  } catch {
    return { view: value, speech: speechFor(spokenValue, locale) };
  }

  if (hasBox(nodes)) {
    const filled = problem.prompt.replace(BOX_MARKUP, spokenValue);
    const speech = trySpeech(filled, locale) ?? `${speechFor(problem.prompt, locale)}: ${speechFor(spokenValue, locale)}`;
    return { view: toMathView(nodes, { fill: value }), speech };
  }

  const lhs = revealLhs(problem, nodes);
  if (lhs === null) return { view: value, speech: speechFor(spokenValue, locale) };
  const view: MathView[] = [...markupView(lhs), { kind: 'space' }, { kind: 'op', text: '=' }, { kind: 'space' }, ...value];
  return { view, speech: speechFor(`${lhs} = ${spokenValue}`, locale) };
}
